import {
  LayoutGrid,
  FolderKanban,
  Server,
  ScrollText,
  Settings,
  Bot,
  Cpu,
  Boxes,
  Globe,
  ShieldCheck,
  GitBranch,
  Rocket,
  History,
  Plug,
  CirclePlay,
  Users,
  Cloud,
  Gauge,
  Vault,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import {
  isDashboardPath,
  isOpsDomainCertsPath,
  isOpsRuntimePath,
  isOpsServersInventoryPath,
  isOpsServicesPath,
  isOpsSitesPath,
  isOpsTimelinePath,
  isProjectsListPath,
  isProjectsReposPath,
  isProjectsReleasesPath,
  isProjectsRunsPath,
  isSettingsCloudflarePath,
  isSettingsGeneralPath,
  isSettingsTeamsPath,
  PROJECTS_HUB_PATHS,
  PUBLIC_STATUS_PATH,
  SERVERS_HUB_PATHS,
  SETTINGS_PATHS,
} from './nav-paths.ts';

export interface SpatialNavItem {
  to: string;
  labelKey: string;
  icon: LucideIcon;
  end?: boolean;
  isActive?: (pathname: string) => boolean;
}

export interface SpatialNavSection {
  labelKey: string;
  items: SpatialNavItem[];
}

/** Always-visible audit cluster. Everything else sits behind More. */
export const spatialDockPrimary: SpatialNavItem[] = [
  {
    to: '/',
    labelKey: 'sidebar.dashboard',
    icon: LayoutGrid,
    isActive: isDashboardPath,
  },
  {
    to: SERVERS_HUB_PATHS.list,
    labelKey: 'sidebar.servers',
    icon: Server,
    end: true,
    isActive: isOpsServersInventoryPath,
  },
  {
    to: SERVERS_HUB_PATHS.runtime,
    labelKey: 'sidebar.runtime',
    icon: Cpu,
    isActive: isOpsRuntimePath,
  },
  {
    to: PROJECTS_HUB_PATHS.runs,
    labelKey: 'sidebar.activity',
    icon: CirclePlay,
    isActive: isProjectsRunsPath,
  },
];

export const spatialNavSections: SpatialNavSection[] = [
  {
    labelKey: 'sidebar.sectionWorkspace',
    items: [spatialDockPrimary[0]],
  },
  {
    labelKey: 'sidebar.sectionOps',
    items: [
      spatialDockPrimary[1],
      spatialDockPrimary[2],
      {
        to: SERVERS_HUB_PATHS.services,
        labelKey: 'sidebar.services',
        icon: Boxes,
        isActive: isOpsServicesPath,
      },
      {
        to: SERVERS_HUB_PATHS.sites,
        labelKey: 'sidebar.sites',
        icon: Globe,
        isActive: isOpsSitesPath,
      },
      {
        to: SERVERS_HUB_PATHS.domainCertificates,
        labelKey: 'sidebar.domainCertificates',
        icon: ShieldCheck,
        isActive: isOpsDomainCertsPath,
      },
      {
        to: SERVERS_HUB_PATHS.timeline,
        labelKey: 'sidebar.timeline',
        icon: History,
        isActive: isOpsTimelinePath,
      },
    ],
  },
  {
    labelKey: 'sidebar.sectionDeliver',
    items: [
      {
        to: PROJECTS_HUB_PATHS.list,
        labelKey: 'sidebar.projects',
        icon: FolderKanban,
        end: true,
        isActive: isProjectsListPath,
      },
      {
        to: PROJECTS_HUB_PATHS.repos,
        labelKey: 'sidebar.repos',
        icon: GitBranch,
        isActive: isProjectsReposPath,
      },
      {
        to: PROJECTS_HUB_PATHS.releases,
        labelKey: 'sidebar.releases',
        icon: Rocket,
        isActive: isProjectsReleasesPath,
      },
      spatialDockPrimary[3],
      { to: '/ai-driven', labelKey: 'sidebar.aiDriven', icon: Bot },
      { to: '/mcp-activity', labelKey: 'sidebar.mcpActivity', icon: Plug },
    ],
  },
  {
    labelKey: 'sidebar.sectionPlatform',
    items: [
      { to: '/logs', labelKey: 'sidebar.logs', icon: ScrollText },
      { to: '/secrets', labelKey: 'sidebar.secrets', icon: Vault },
      { to: PUBLIC_STATUS_PATH, labelKey: 'sidebar.publicStatus', icon: Gauge },
    ],
  },
  {
    labelKey: 'sidebar.sectionSettings',
    items: [
      {
        to: SETTINGS_PATHS.general,
        labelKey: 'sidebar.settings',
        icon: Settings,
        end: true,
        isActive: isSettingsGeneralPath,
      },
      {
        to: SETTINGS_PATHS.teams,
        labelKey: 'sidebar.teams',
        icon: Users,
        isActive: isSettingsTeamsPath,
      },
      {
        to: SETTINGS_PATHS.cloudflare,
        labelKey: 'sidebar.cloudflare',
        icon: Cloud,
        isActive: isSettingsCloudflarePath,
      },
    ],
  },
];

const primaryHrefs = new Set(spatialDockPrimary.map((item) => item.to));

export function spatialDockOverflow(): SpatialNavSection[] {
  return spatialNavSections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => !primaryHrefs.has(item.to)),
    }))
    .filter((section) => section.items.length > 0);
}

function itemMatches(item: SpatialNavItem, pathname: string): boolean {
  if (item.isActive) return item.isActive(pathname);
  if (item.end ?? item.to === '/') return pathname === item.to;
  return pathname === item.to || pathname.startsWith(`${item.to}/`);
}

export function isSpatialDockOverflowPath(pathname: string): boolean {
  return spatialDockOverflow().some((section) =>
    section.items.some((item) => itemMatches(item, pathname)),
  );
}
